/**
 * UserManagement Component (VH-19)
 *
 * Displays a list of all users with their roles, and allows
 * super users to change user roles via a dropdown.
 *
 * VH-287: "Add user" (a set-up mail goes out), and per user whether the
 * set-up is finished, with "Send set-up mail again" while it is not.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminAPI } from '../../services/api'
import AddUserForm from './AddUserForm'

const ROLES = ['super_user', 'user', 'coach']

function RoleBadge({ role, t }) {
  const colors = {
    super_user: 'bg-purple-100 text-purple-800',
    user: 'bg-blue-100 text-blue-800',
    coach: 'bg-green-100 text-green-800',
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
      {t(`admin.users.roles.${role}`, role)}
    </span>
  )
}

export default function UserManagement() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [changingRoleFor, setChangingRoleFor] = useState(null)
  const [adding, setAdding] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminAPI.listUsers({ skip: 0, limit: 100 }),
    select: (response) => response.data,
  })

  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => adminAPI.changeUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(t('admin.users.roleChanged'))
      setChangingRoleFor(null)
    },
    onError: (error) => {
      const detail = error.response?.data?.detail
      if (detail && detail.includes('last super user')) {
        toast.error(t('admin.users.lastSuperUserWarning'))
      } else {
        toast.error(t('admin.users.roleChangeFailed'))
      }
      setChangingRoleFor(null)
    },
  })

  const sendMailMutation = useMutation({
    mutationFn: (user) => adminAPI.sendSetupMail(user.id),
    onSuccess: ({ data }, user) => {
      if (data.sent) toast.success(t('admin.users.setup.sent', { email: user.email }))
      else toast.error(t('admin.users.setup.notSent', { email: user.email }))
    },
    onError: (_error, user) => toast.error(t('admin.users.setup.notSent', { email: user.email })),
  })

  // VH-317 (HP-56 D7): any admin may unlink a user's HubPlatform account.
  const unlinkMutation = useMutation({
    mutationFn: (user) => adminAPI.unlinkHubPlatform(user.id),
    onSuccess: (_response, user) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      toast.success(t('admin.users.hubplatform.unlinked', { email: user.email }))
    },
    onError: (_error, user) => toast.error(t('admin.users.hubplatform.unlinkFailed', { email: user.email })),
  })

  const handleUnlink = (user) => {
    if (window.confirm(t('admin.users.hubplatform.confirm', { email: user.email }))) {
      unlinkMutation.mutate(user)
    }
  }

  const handleSendMail = (user) => {
    if (window.confirm(t('admin.users.setup.confirm', { email: user.email }))) {
      sendMailMutation.mutate(user)
    }
  }

  const handleRoleChange = (user, newRole) => {
    if (newRole === user.role) return

    const confirmed = window.confirm(
      t('admin.users.confirmRoleChange', {
        email: user.email,
        oldRole: t(`admin.users.roles.${user.role}`, user.role),
        newRole: t(`admin.users.roles.${newRole}`, newRole),
      })
    )

    if (confirmed) {
      setChangingRoleFor(user.id)
      changeRoleMutation.mutate({ userId: user.id, role: newRole })
    }
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-6"></div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <p className="ml-3 text-sm text-red-700">{t('common.error')}</p>
        </div>
      </div>
    )
  }

  const users = data?.users || []

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('admin.users.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('admin.users.subtitle')}</p>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary">
            {t('admin.users.add.open')}
          </button>
        )}
      </div>

      {adding && <AddUserForm onClose={() => setAdding(false)} />}

      {users.length === 0 ? (
        <p className="text-sm text-gray-500">{t('admin.users.noUsers')}</p>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  {t('admin.users.email')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('admin.users.username')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('admin.users.role')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('admin.users.joined')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('admin.users.setup.column')}
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-surface-container">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                    {user.email}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {user.username}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <RoleBadge role={user.role} t={t} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    {user.has_second_factor ? (
                      <span className="text-green-700">{t('admin.users.setup.done')}</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-700">{t('admin.users.setup.open')}</span>
                        <button
                          type="button"
                          onClick={() => handleSendMail(user)}
                          disabled={sendMailMutation.isPending}
                          className="text-primary-600 hover:text-primary-500 disabled:opacity-50"
                        >
                          {t('admin.users.setup.sendAgain')}
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="flex items-center gap-3">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value)}
                        disabled={changingRoleFor === user.id}
                        className="text-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {t(`admin.users.roles.${role}`, role)}
                          </option>
                        ))}
                      </select>
                      {user.hubplatform_linked && (
                        <button
                          type="button"
                          onClick={() => handleUnlink(user)}
                          disabled={unlinkMutation.isPending}
                          className="text-primary-600 hover:text-primary-500 disabled:opacity-50"
                        >
                          {t('admin.users.hubplatform.unlink')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.total > 0 && (
        <p className="mt-3 text-sm text-gray-500">
          {data.total} {data.total === 1 ? 'user' : 'users'}
        </p>
      )}
    </div>
  )
}
